"use client";

import { cn } from "@/lib/utils";
import { useMarket } from "@app/_context";
import useTranslate from "@app/_hooks/useTranslate";
import { Button } from "@ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@ui/field";
import { Heading } from "@ui/heading";
import { Paragraph } from "@ui/paragraph";
import { RadioItem } from "@ui/radio";
import { Loader2 } from "lucide-react";
import type { ComponentProps } from "react";
import useRegisterAccountForm from "../hooks/useRegisterAccountForm";
import getFieldErrorState from "../utils/getFieldErrorState";
import getMarketDialCode from "../utils/getMarketDialCode";
import AuthToggle from "./AuthToggle";

export default function RegisterForm({
  className,
  ...props
}: ComponentProps<"div">) {
  const t = useTranslate();
  const { market } = useMarket().state;
  const form = useRegisterAccountForm();
  const dialCode = getMarketDialCode(market.code);
  const phonePlaceholder = `${dialCode} …`;

  return (
    <div className={cn("flex flex-col", className)} {...props}>
      <Heading className="mb-8 text-2xl" as="h1">
        {t("register")}
      </Heading>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          form.handleSubmit();
        }}
      >
        <FieldSet>
          <FieldLegend>{t("accountType")}</FieldLegend>
          <form.AppField
            name="isCompany"
            children={(field) => (
              <field.RadioGroup
                name={field.name}
                value={String(field.state.value)}
                className="mb-4"
                onValueChange={(value) => field.handleChange(value === "true")}
              >
                <Field orientation="horizontal">
                  <RadioItem value="false" id="private" />
                  <FieldLabel htmlFor="private">{t("individual")}</FieldLabel>
                </Field>
                <Field orientation="horizontal">
                  <RadioItem value="true" id="company" />
                  <FieldLabel htmlFor="company">{t("company")}</FieldLabel>
                </Field>
              </field.RadioGroup>
            )}
          />
          <form.AppField
            name="email"
            children={(field) => (
              <Field
                invalid={field.getMeta().isDirty && !field.getMeta().isValid}
                dirty={field.getMeta().isDirty}
                touched={field.getMeta().isTouched}
              >
                <FieldLabel placeholder>{t("formEmailLabel")}</FieldLabel>
                <field.Input
                  required
                  autoComplete="email"
                  placeholder={t("formEmailLabel")}
                  type="email"
                  value={field.state.value}
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={() => field.handleBlur()}
                />
                {getFieldErrorState(field) && (
                  <FieldError>{t(field.getMeta().errors[0]!)}</FieldError>
                )}
              </Field>
            )}
          />
          <FieldGroup className="md:flex-row">
            <form.AppField
              name="password"
              children={(field) => (
                <Field
                  invalid={field.getMeta().isDirty && !field.getMeta().isValid}
                  dirty={field.getMeta().isDirty}
                  touched={field.getMeta().isTouched}
                >
                  <FieldLabel placeholder>{t("formPasswordLabel")}</FieldLabel>
                  <field.Input
                    required
                    autoComplete="new-password"
                    placeholder={t("formPasswordLabel")}
                    type="password"
                    value={field.state.value}
                    onChange={(event) => field.handleChange(event.target.value)}
                    onBlur={() => field.handleBlur()}
                  />
                  {getFieldErrorState(field) && (
                    <FieldError>{t(field.getMeta().errors[0]!)}</FieldError>
                  )}
                  <FieldDescription>
                    {t("passwordRequirements")}
                  </FieldDescription>
                </Field>
              )}
            />
            <form.AppField
              name="repeatPassword"
              children={(field) => (
                <Field
                  invalid={field.getMeta().isDirty && !field.getMeta().isValid}
                  dirty={field.getMeta().isDirty}
                  touched={field.getMeta().isTouched}
                >
                  <FieldLabel placeholder>
                    {t("formRepeatPasswordLabel")}
                  </FieldLabel>
                  <field.Input
                    required
                    placeholder={t("formRepeatPasswordLabel")}
                    type="password"
                    value={field.state.value}
                    onChange={(event) => field.handleChange(event.target.value)}
                    onBlur={() => field.handleBlur()}
                  />
                  {getFieldErrorState(field) && (
                    <FieldError>{t(field.getMeta().errors[0]!)}</FieldError>
                  )}
                </Field>
              )}
            />
          </FieldGroup>
          <FieldGroup className="md:flex-row">
            <form.AppField
              name="firstName"
              children={(field) => (
                <Field
                  invalid={field.getMeta().isDirty && !field.getMeta().isValid}
                  dirty={field.getMeta().isDirty}
                  touched={field.getMeta().isTouched}
                >
                  <FieldLabel placeholder>{t("formFirstNameLabel")}</FieldLabel>
                  <field.Input
                    autoComplete="given-name"
                    placeholder={t("formFirstNameLabel")}
                    required
                    value={field.state.value}
                    onChange={(event) => field.handleChange(event.target.value)}
                    onBlur={() => field.handleBlur()}
                  />
                  {getFieldErrorState(field) && (
                    <FieldError>{t(field.getMeta().errors[0]!)}</FieldError>
                  )}
                </Field>
              )}
            />
            <form.AppField
              name="lastName"
              children={(field) => (
                <Field
                  invalid={field.getMeta().isDirty && !field.getMeta().isValid}
                  dirty={field.getMeta().isDirty}
                  touched={field.getMeta().isTouched}
                >
                  <FieldLabel placeholder>{t("formLastNameLabel")}</FieldLabel>
                  <field.Input
                    autoComplete="family-name"
                    placeholder={t("formLastNameLabel")}
                    required
                    value={field.state.value}
                    onChange={(event) => field.handleChange(event.target.value)}
                    onBlur={() => field.handleBlur()}
                  />
                  {getFieldErrorState(field) && (
                    <FieldError>{t(field.getMeta().errors[0]!)}</FieldError>
                  )}
                </Field>
              )}
            />
          </FieldGroup>
          <form.AppField
            name="phoneNumber"
            children={(field) => (
              <Field
                invalid={field.getMeta().isDirty && !field.getMeta().isValid}
                dirty={field.getMeta().isDirty}
                touched={field.getMeta().isTouched}
              >
                <FieldLabel placeholder>{t("formPhoneNumberLabel")}</FieldLabel>
                <field.Input
                  required
                  autoComplete="tel"
                  placeholder={t("formPhoneNumberLabel") + phonePlaceholder}
                  type="tel"
                  value={field.state.value}
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={() => field.handleBlur()}
                />
                {getFieldErrorState(field) && (
                  <FieldError>{t(field.getMeta().errors[0]!)}</FieldError>
                )}
              </Field>
            )}
          />
          <form.Subscribe
            selector={(state) => state.values.isCompany}
            children={(isCompany) =>
              isCompany ? (
                <FieldGroup className="md:flex-row">
                  <form.AppField
                    name="companyName"
                    children={(field) => (
                      <Field
                        invalid={
                          field.getMeta().isDirty && !field.getMeta().isValid
                        }
                        dirty={field.getMeta().isDirty}
                        touched={field.getMeta().isTouched}
                      >
                        <FieldLabel placeholder>
                          {t("formCompanyNameLabel")}
                        </FieldLabel>
                        <field.Input
                          autoComplete="organization"
                          placeholder={t("formCompanyNameLabel")}
                          value={field.state.value ?? ""}
                          onChange={(event) =>
                            field.handleChange(event.target.value)
                          }
                          onBlur={() => field.handleBlur()}
                        />
                        {getFieldErrorState(field) && (
                          <FieldError>
                            {t(field.getMeta().errors[0]!)}
                          </FieldError>
                        )}
                      </Field>
                    )}
                  />
                  <form.AppField
                    name="companyOrganizationalNumber"
                    children={(field) => (
                      <Field
                        invalid={
                          field.getMeta().isDirty && !field.getMeta().isValid
                        }
                        dirty={field.getMeta().isDirty}
                        touched={field.getMeta().isTouched}
                      >
                        <FieldLabel placeholder>
                          {t("formCompanyOrganizationalNumberLabel")}
                        </FieldLabel>
                        <field.Input
                          placeholder={t(
                            "formCompanyOrganizationalNumberLabel",
                          )}
                          value={field.state.value ?? ""}
                          onChange={(event) =>
                            field.handleChange(event.target.value)
                          }
                          onBlur={() => field.handleBlur()}
                        />
                        {getFieldErrorState(field) && (
                          <FieldError>
                            {t(field.getMeta().errors[0]!)}
                          </FieldError>
                        )}
                      </Field>
                    )}
                  />
                </FieldGroup>
              ) : (
                <form.AppField
                  name="birthDate"
                  children={(field) => (
                    <Field
                      invalid={
                        field.getMeta().isDirty && !field.getMeta().isValid
                      }
                      dirty={field.getMeta().isDirty}
                      touched={field.getMeta().isTouched}
                    >
                      <FieldLabel placeholder>
                        {t("formBirthDateLabel")} (yyyy-mm-dd)
                      </FieldLabel>
                      <field.Input
                        autoComplete="bday"
                        placeholder={t("formBirthDateLabel")}
                        value={field.state.value ?? ""}
                        onChange={(event) =>
                          field.handleChange(event.target.value)
                        }
                        onBlur={() => field.handleBlur()}
                      />
                      {getFieldErrorState(field) && (
                        <FieldError>{t(field.getMeta().errors[0]!)}</FieldError>
                      )}
                    </Field>
                  )}
                />
              )
            }
          />
          <form.AppField
            name="street"
            children={(field) => (
              <Field
                invalid={field.getMeta().isDirty && !field.getMeta().isValid}
                dirty={field.getMeta().isDirty}
                touched={field.getMeta().isTouched}
              >
                <FieldLabel placeholder>{t("street")}</FieldLabel>
                <field.Input
                  autoComplete="street-address"
                  placeholder={t("street")}
                  value={field.state.value ?? ""}
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={() => field.handleBlur()}
                />
                {getFieldErrorState(field) && (
                  <FieldError>{t(field.getMeta().errors[0]!)}</FieldError>
                )}
              </Field>
            )}
          />
          <FieldGroup className="flex-row">
            <form.AppField
              name="zipCode"
              children={(field) => (
                <Field
                  invalid={field.getMeta().isDirty && !field.getMeta().isValid}
                  dirty={field.getMeta().isDirty}
                  touched={field.getMeta().isTouched}
                >
                  <FieldLabel placeholder>{t("zipCode")}</FieldLabel>
                  <field.Input
                    autoComplete="postal-code"
                    placeholder={t("zipCode")}
                    value={field.state.value ?? ""}
                    onChange={(event) => field.handleChange(event.target.value)}
                    onBlur={() => field.handleBlur()}
                  />
                  {getFieldErrorState(field) && (
                    <FieldError>{t(field.getMeta().errors[0]!)}</FieldError>
                  )}
                </Field>
              )}
            />
            <form.AppField
              name="city"
              children={(field) => (
                <Field
                  invalid={field.getMeta().isDirty && !field.getMeta().isValid}
                  dirty={field.getMeta().isDirty}
                  touched={field.getMeta().isTouched}
                >
                  <FieldLabel placeholder>{t("city")}</FieldLabel>
                  <field.Input
                    autoComplete="address-level2"
                    placeholder={t("city")}
                    value={field.state.value ?? ""}
                    onChange={(event) => field.handleChange(event.target.value)}
                    onBlur={() => field.handleBlur()}
                  />
                  {getFieldErrorState(field) && (
                    <FieldError>{t(field.getMeta().errors[0]!)}</FieldError>
                  )}
                </Field>
              )}
            />
          </FieldGroup>
          <FieldGroup className="py-4">
            <form.AppField
              name="acceptsEmail"
              children={(field) => (
                <Field orientation="horizontal">
                  <field.Checkbox
                    id={field.name}
                    checked={field.state.value ?? false}
                    onCheckedChange={(event) =>
                      field.handleChange(Boolean(event))
                    }
                  />
                  <FieldLabel htmlFor={field.name}>
                    {t("subscribeToNewsletter")}
                  </FieldLabel>
                </Field>
              )}
            />
            <form.AppField
              name="acceptsSms"
              children={(field) => (
                <Field orientation="horizontal">
                  <field.Checkbox
                    id={field.name}
                    checked={field.state.value ?? false}
                    onCheckedChange={(event) =>
                      field.handleChange(Boolean(event))
                    }
                  />
                  <FieldLabel htmlFor={field.name}>
                    {t("acceptSmsOffers")}
                  </FieldLabel>
                </Field>
              )}
            />
          </FieldGroup>
          <form.Subscribe
            selector={(state) => [
              state.isValid,
              state.isDirty,
              state.isSubmitting,
            ]}
            children={([isValid, isDirty, isSubmitting]) => (
              <Button
                className="w-full"
                type="submit"
                disabled={!isDirty || !isValid || isSubmitting}
              >
                {isSubmitting ? (
                  <Loader2 className="size-5 animate-spin" strokeWidth={2} />
                ) : (
                  t("createAccount")
                )}
              </Button>
            )}
          />
        </FieldSet>
        <form.Subscribe
          selector={(state) => state.errors}
          children={(errors) => (
            <>
              {errors.map((error) =>
                error === "errorRegistrationSignInFailed" ? (
                  <form.FormError
                    key="errorRegistrationSignInFailed"
                    type="error"
                    className="mt-4"
                  >
                    {t("alreadyHaveAnAccount")}
                  </form.FormError>
                ) : (
                  <form.FormError
                    key={error as string}
                    type="error"
                    className="mt-4"
                  >
                    {t(error as string)}
                  </form.FormError>
                ),
              )}
              {errors.includes("errorRegistrationSignInFailed") && (
                <div className="mt-4 flex flex-col items-center gap-2">
                  <AuthToggle />
                </div>
              )}
            </>
          )}
        />
      </form>
      <Paragraph className="mt-4">{t("registerFormTermsText")}</Paragraph>
    </div>
  );
}
